import { useEffect, useState } from 'react';
import { Loader2, ExternalLink, CheckCircle2, Clock, AlertCircle, Zap } from 'lucide-react';
import pb from '../lib/pocketbase';
import BrandingPanel, { parseBrandingData, BrandingData } from '../components/BrandingPanel';
import { ADMIN_KEY, API_URL } from '../lib/config';

interface Order {
  id: string;
  created: string;
  business_name: string;
  contact_name: string;
  contact_email: string;
  phone: string;
  activity: string;
  city: string;
  plan: string;
  addons: string;
  payment_status: string;
  paid_at: string;
  site_status: string;
  site_url: string;
  site_project: string;
  domain_name: string;
  domain_registered: boolean;
  logo_needed: boolean;
  logo_generated: boolean;
  logo_url: string;
  built_at: string;
  validated: boolean;
  validated_at: string;
  live_email_sent: boolean;
  promo_code: string;
  monthly_amount: number;
  total_oneshot: number;
  branding_data: string | object | null;
  validation_status: Record<string, any> | null;
  logo_validated: boolean;
  gmb_status: string;
  gmb_location_id: string;
  gmb_verified_at: string;
  location: string;
}

const STATUS_STEPS = [
  { key: 'payment', label: 'Paiement', icon: '💳' },
  { key: 'branding', label: 'Analyse branding', icon: '🎨' },
  { key: 'questions', label: 'Questions client', icon: '❓' },
  { key: 'generation', label: 'Génération site', icon: '🔨' },
  { key: 'deployment', label: 'Déploiement', icon: '🚀' },
  { key: 'review', label: 'Validation', icon: '👀' },
  { key: 'live', label: 'En ligne', icon: '✅' },
];

function getStepStatus(order: Order, stepKey: string): 'done' | 'active' | 'pending' | 'error' {
  switch (stepKey) {
    case 'payment':
      return order.payment_status === 'paid' ? 'done' : order.payment_status === 'expired' ? 'error' : 'pending';
    case 'branding':
      if (order.site_status === 'error') return 'error';
      if (['building', 'built', 'built_auto', 'live', 'waiting_client', 'client_responded'].includes(order.site_status)) return 'done';
      if (order.payment_status === 'paid' && (!order.site_status || order.site_status === 'pending')) return 'active';
      return 'pending';
    case 'questions':
      if (order.site_status === 'waiting_client') return 'active';
      if (['client_responded', 'building', 'built', 'built_auto', 'live'].includes(order.site_status)) return 'done';
      if (!order.site_status || order.site_status === 'pending') return 'pending';
      return 'done'; // skip if no questions needed
    case 'generation':
      if (order.site_status === 'error') return 'error';
      if (['built', 'built_auto', 'live'].includes(order.site_status)) return 'done';
      if (order.site_status === 'building' || order.site_status === 'client_responded') return 'active';
      if (order.site_status === 'waiting_client') return 'pending';
      return 'pending';
    case 'deployment':
      if (['built', 'built_auto', 'live'].includes(order.site_status) && order.site_url) return 'done';
      if (order.site_status === 'building') return 'active';
      return 'pending';
    case 'review':
      if (order.validated) return 'done';
      if (order.site_url && ['built', 'built_auto'].includes(order.site_status)) return 'active';
      return 'pending';
    case 'live':
      if (order.validated && order.live_email_sent) return 'done';
      if (order.validated) return 'active';
      return 'pending';
    default:
      return 'pending';
  }
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  } catch { return dateStr; }
}

function parseAddons(addons: string | string[] | null | undefined): string[] {
  if (!addons) return [];
  if (Array.isArray(addons)) return addons;
  try {
    const parsed = JSON.parse(addons);
    return Array.isArray(parsed) ? parsed : [String(parsed)];
  } catch {
    return addons ? [String(addons)] : [];
  }
}

const PB_URL = 'https://api.simpleclair.fr';

/**
 * Resolve logo URL — handles PB file URLs, absolute URLs, and legacy /logos/ paths
 */
function resolveLogoUrl(order: Order): string {
  const url = order.logo_url;
  if (!url) return '';
  // Already an absolute URL (PB or external)
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  // PB file path (/api/files/...)
  if (url.startsWith('/api/files/')) return `${PB_URL}${url}`;
  // Legacy /logos/ path — try constructing PB URL from record
  if (url.startsWith('/logos/')) {
    // Fallback: try PB files API if the record has a logo file field
    return `${PB_URL}/api/files/submissions/${order.id}/${url.split('/').pop()}`;
  }
  return url;
}

const addonLabels: Record<string, string> = {
  logo: '🎨 Logo',
  blog: '📝 Blog',
  form: '📋 Formulaire',
  domain: '🌐 Domaine',
  gmb: '📍 GMB',
  gmb_creation: '📍 Création GMB',
  gmb_optim: '📍 Optim GMB',
  visibility_standard: '🚀 Visibilité',
};

// Rejection modal component
function RejectModal({ component, onConfirm, onCancel }: { 
  component: string; 
  onConfirm: (reason: string) => void; 
  onCancel: () => void;
}) {
  const [reason, setReason] = useState('');
  const label = component === 'site' ? 'site' : component === 'logo' ? 'logo' : component;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
    }} onClick={onCancel}>
      <div style={{
        background: '#fff', borderRadius: 16, padding: 28, width: '90%', maxWidth: 480,
        boxShadow: '0 8px 30px rgba(0,0,0,0.15)'
      }} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 16px', fontSize: 18, color: '#1e293b' }}>
          Pourquoi refuser le {label} ?
        </h3>
        <textarea
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="Ex: Le logo est trop sombre, changez la couleur..."
          rows={4}
          style={{
            width: '100%', padding: 12, border: '1px solid #e2e8f0', borderRadius: 10,
            fontSize: 14, resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box',
            outline: 'none'
          }}
          autoFocus
        />
        <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            style={{
              padding: '10px 20px', borderRadius: 8, border: '1px solid #e2e8f0',
              background: '#f8fafc', color: '#64748b', fontSize: 14, cursor: 'pointer', fontWeight: 500
            }}
          >
            Annuler
          </button>
          <button
            onClick={() => { if (reason.trim()) onConfirm(reason.trim()); }}
            disabled={!reason.trim()}
            style={{
              padding: '10px 20px', borderRadius: 8, border: 'none',
              background: reason.trim() ? '#ef4444' : '#fca5a5', color: '#fff',
              fontSize: 14, cursor: reason.trim() ? 'pointer' : 'not-allowed', fontWeight: 600
            }}
          >
            Envoyer les remarques
          </button>
        </div>
      </div>
    </div>
  );
}

// Helper to extract validation status (supports both old string and new object format)
function getValidationInfo(vs: Record<string, any>, component: string): { status: string; reason?: string } {
  const val = vs[component];
  if (!val) return { status: 'pending' };
  if (typeof val === 'string') return { status: val };
  if (typeof val === 'object' && val.status) return { status: val.status, reason: val.reason };
  return { status: 'pending' };
}

export default function Orders() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedOrder, setExpandedOrder] = useState<string | null>(null);
  const [rejectModal, setRejectModal] = useState<{ orderId: string; component: string } | null>(null);
  const [blogArticles, setBlogArticles] = useState<Record<string, any[]>>({});
  const [blogLoading, setBlogLoading] = useState<Record<string, boolean>>({});
  const [visibilityKeywords, setVisibilityKeywords] = useState<Record<string, string[]>>({});
  const [keywordSaving, setKeywordSaving] = useState<Record<string, boolean>>({});

  useEffect(() => {
    loadOrders();
  }, []);

  async function loadOrders() {
    try {
      setLoading(true);
      const result = await pb.collection('submissions').getList(1, 50, {
        filter: "payment_status = 'paid'",
        sort: '-paid_at',
      });
      const items = result.items as unknown as Order[];
      setOrders(items);
      // Pre-fetch blog articles for all orders with blog addon
      for (const order of items) {
        const addons = parseAddons(order.addons);
        if (addons.includes('blog')) {
          fetchBlogArticles(order.id);
        }
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function validateComponent(orderId: string, component: string, action: 'approve' | 'reject', reason?: string) {
    try {
      const body: any = { submissionId: orderId, component, action, adminKey: ADMIN_KEY };
      if (reason) body.reason = reason;
      const res = await fetch(`${API_URL}/admin/validate-component`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (data.ok) {
        if (data.allApproved) {
          alert('🎉 Toutes les composantes validées ! Email envoyé au client.');
        } else if (action === 'reject') {
          const label = component === 'site' ? 'Site' : component === 'logo' ? 'Logo' : component;
          alert(`❌ ${label} refusé — remarques envoyées à l'équipe`);
        } else {
          alert(`✅ ${component === 'site' ? 'Site' : component === 'logo' ? 'Logo' : component} validé`);
        }
      } else {
        alert('❌ Erreur: ' + (data.error || 'Inconnu'));
      }
      await loadOrders();
    } catch (err: any) {
      alert('Erreur: ' + err.message);
    }
  }

  async function fetchBlogArticles(submissionId: string) {
    try {
      setBlogLoading(prev => ({ ...prev, [submissionId]: true }));
      const result = await pb.collection('articles').getList(1, 50, {
        filter: `submission_id='${submissionId}'`,
        sort: '-created',
      });
      setBlogArticles(prev => ({ ...prev, [submissionId]: result.items }));
    } catch (err) {
      console.error('Failed to fetch blog articles:', err);
    } finally {
      setBlogLoading(prev => ({ ...prev, [submissionId]: false }));
    }
  }

  async function fetchVisibilityKeywords(submissionId: string) {
    try {
      const result = await pb.collection('submissions').getOne(submissionId, { fields: 'visibility_keywords' });
      if (result.visibility_keywords) {
        const kw = typeof result.visibility_keywords === 'string'
          ? JSON.parse(result.visibility_keywords)
          : result.visibility_keywords;
        setVisibilityKeywords(prev => ({ ...prev, [submissionId]: Array.isArray(kw) ? kw : [] }));
      }
    } catch (err) {
      // Field may not exist yet, that's fine
    }
  }

  async function saveVisibilityKeywords(submissionId: string, keywords: string[]) {
    try {
      setKeywordSaving(prev => ({ ...prev, [submissionId]: true }));
      await pb.collection('submissions').update(submissionId, {
        visibility_keywords: JSON.stringify(keywords.filter(k => k.trim())),
      });
      alert('✅ Mots-clés sauvegardés');
    } catch (err: any) {
      alert('Erreur: ' + err.message);
    } finally {
      setKeywordSaving(prev => ({ ...prev, [submissionId]: false }));
    }
  }

  async function validateAll(orderId: string) {
    if (!confirm('Tout valider ? Le client recevra un email avec son site en ligne.')) return;
    try {
      const res = await fetch(`${API_URL}/admin/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ submissionId: orderId, adminKey: ADMIN_KEY })
      });
      const data = await res.json();
      if (data.ok) {
        alert(data.emailSent ? '🎉 Tout validé ! Email envoyé au client.' : '✅ Tout validé (email non envoyé)');
      } else {
        alert('❌ Erreur: ' + (data.error || 'Inconnu'));
      }
      await loadOrders();
    } catch (err: any) {
      alert('Erreur: ' + err.message);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-100px)]">
        <div className="flex items-center gap-3 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm">Chargement des commandes...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm">
          Erreur: {error}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Alert: sites to validate */}
      {(() => {
        const toValidate = orders.filter((o: any) => ['built', 'built_auto'].includes(o.site_status) && o.site_url);
        if (toValidate.length === 0) return null;
        return (
          <div className="mb-4 bg-orange-50 border-2 border-orange-300 rounded-xl p-4 flex items-center gap-3">
            <span className="text-2xl">🚨</span>
            <div>
              <p className="font-bold text-orange-800">{toValidate.length} site{toValidate.length > 1 ? 's' : ''} en attente de validation</p>
              <p className="text-sm text-orange-600">{toValidate.map((o: any) => o.business_name).join(', ')}</p>
            </div>
          </div>
        );
      })()}

      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Zap className="w-6 h-6 text-orange-500" />
            Commandes
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            {orders.length} commande{orders.length > 1 ? 's' : ''} payée{orders.length > 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={loadOrders}
          className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors"
        >
          🔄 Actualiser
        </button>
      </div>

      {orders.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-100 shadow-sm">
          <div className="text-4xl mb-4">📦</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucune commande</h3>
          <p className="text-gray-500">Les commandes payées apparaîtront ici</p>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map(order => {
            const addons = parseAddons(order.addons);
            const isExpanded = expandedOrder === order.id;
            
            return (
              <div key={order.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                {/* Order header */}
                <div
                  className="p-5 cursor-pointer hover:bg-gray-50/50 transition-colors"
                  onClick={() => {
                    const newExpanded = isExpanded ? null : order.id;
                    setExpandedOrder(newExpanded);
                    if (newExpanded) {
                      const orderAddons = parseAddons(order.addons);
                      if (orderAddons.includes('blog') && !blogArticles[order.id]) {
                        fetchBlogArticles(order.id);
                      }
                      if (orderAddons.includes('visibility_standard') && !visibilityKeywords[order.id]) {
                        fetchVisibilityKeywords(order.id);
                      }
                    }
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-orange-50 rounded-xl flex items-center justify-center text-xl">
                        🏢
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{order.business_name || 'N/A'}</h3>
                        <p className="text-sm text-gray-500">
                          {order.contact_name} · {order.activity} · {order.city}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {order.site_url && ['built', 'built_auto'].includes(order.site_status) && !order.validated && (
                        <button
                          onClick={(e) => { e.stopPropagation(); window.open(order.site_url, '_blank'); }}
                          className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors flex items-center gap-1.5"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                          Voir
                        </button>
                      )}
                      {order.validated && (
                        <span className="px-3 py-1.5 bg-green-50 text-green-600 rounded-lg text-sm font-medium">✅ Validé</span>
                      )}
                      <span className="text-xs text-gray-400">
                        {formatDate(order.paid_at || order.created)}
                      </span>
                    </div>
                  </div>

                  {/* Progress steps */}
                  <div className="flex items-center gap-1 mt-4">
                    {STATUS_STEPS.map((step, i) => {
                      const status = getStepStatus(order, step.key);
                      return (
                        <div key={step.key} className="flex items-center flex-1">
                          <div className={`
                            flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium flex-1 justify-center
                            ${status === 'done' ? 'bg-green-50 text-green-700' : ''}
                            ${status === 'active' ? 'bg-orange-50 text-orange-700 animate-pulse' : ''}
                            ${status === 'pending' ? 'bg-gray-50 text-gray-400' : ''}
                            ${status === 'error' ? 'bg-red-50 text-red-600' : ''}
                          `}>
                            <span>{step.icon}</span>
                            <span className="hidden lg:inline">{step.label}</span>
                          </div>
                          {i < STATUS_STEPS.length - 1 && (
                            <div className={`w-4 h-0.5 mx-0.5 ${status === 'done' ? 'bg-green-300' : 'bg-gray-200'}`} />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Expanded details */}
                {isExpanded && (
                  <div className="px-5 pb-5 border-t border-gray-100 pt-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-400 block text-xs mb-1">Email</span>
                        <span className="text-gray-700">{order.contact_email || '-'}</span>
                      </div>
                      <div>
                        <span className="text-gray-400 block text-xs mb-1">Téléphone</span>
                        <span className="text-gray-700">{order.phone || '-'}</span>
                      </div>
                      <div>
                        <span className="text-gray-400 block text-xs mb-1">Plan</span>
                        <span className="text-gray-700">{order.plan === 'oneshot' ? 'Clé en main' : 'SaaS'}</span>
                      </div>
                      <div>
                        <span className="text-gray-400 block text-xs mb-1">Promo</span>
                        <span className="text-gray-700">{order.promo_code || '-'}</span>
                      </div>
                    </div>

                    {addons.length > 0 && (
                      <div className="mt-3">
                        <span className="text-gray-400 text-xs block mb-1.5">Options</span>
                        <div className="flex flex-wrap gap-2">
                          {addons.map(addon => (
                            <span key={addon} className="px-2.5 py-1 bg-gray-50 rounded-lg text-xs text-gray-600">
                              {addonLabels[addon] || addon}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {order.site_url && (
                      <div className="mt-3">
                        <span className="text-gray-400 text-xs block mb-1.5">URL du site</span>
                        <a href={order.site_url} target="_blank" className="text-blue-600 text-sm hover:underline flex items-center gap-1">
                          {order.site_url} <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    )}

                    {order.domain_name && (
                      <div className="mt-3">
                        <span className="text-gray-400 text-xs block mb-1.5">Domaine</span>
                        <span className="text-gray-700 text-sm">
                          {order.domain_name} {order.domain_registered ? '✅ Enregistré' : '⏳ En attente'}
                        </span>
                      </div>
                    )}

                    {/* Logo */}
                    {order.logo_url && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-xl">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-xs font-semibold text-gray-500">🎨 Logo généré</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="bg-white rounded-xl border border-gray-200 p-3 flex items-center justify-center" style={{width: 120, height: 120}}>
                            <img src={resolveLogoUrl(order)} alt="Logo" className="max-w-full max-h-full object-contain" />
                          </div>
                          <div className="bg-gray-800 rounded-xl p-3 flex items-center justify-center" style={{width: 120, height: 120}}>
                            <img src={resolveLogoUrl(order)} alt="Logo fond sombre" className="max-w-full max-h-full object-contain" />
                          </div>
                        </div>
                      </div>
                    )}
                    {order.logo_generated && !order.logo_url && (
                      <div className="mt-4 p-3 bg-amber-50 border border-amber-100 rounded-xl">
                        <span className="text-xs text-amber-600">⚠️ Logo généré mais URL non disponible</span>
                      </div>
                    )}

                    {/* Nina's Branding Analysis */}
                    {(() => {
                      const bd = parseBrandingData(order.branding_data);
                      return bd ? <div className="mt-4"><BrandingPanel data={bd} /></div> : null;
                    })()}

                    {/* Validation modulaire */}
                    {['built', 'built_auto'].includes(order.site_status) && !order.validated && (
                      <div className="mt-4 p-4 bg-orange-50 border-2 border-orange-200 rounded-xl">
                        <p className="font-semibold text-orange-800 mb-3">📋 Validation de la commande</p>
                        <div className="space-y-2">
                          {/* Site */}
                          {order.site_url && (() => {
                            const vs = order.validation_status || {};
                            const info = getValidationInfo(vs, 'site');
                            return (
                              <div className="bg-white rounded-lg p-3">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <span>🌐</span>
                                    <span className="text-sm font-medium">Site internet</span>
                                    {info.status === 'approved' && <span className="text-xs text-green-600 font-medium">✅</span>}
                                    {info.status === 'rejected' && <span className="text-xs text-red-600 font-medium">❌ À refaire</span>}
                                  </div>
                                  {info.status === 'pending' && (
                                    <div className="flex gap-1.5">
                                      <button onClick={(e) => { e.stopPropagation(); window.open(order.site_url, '_blank'); }} className="px-2 py-1 text-xs bg-blue-50 text-blue-600 rounded hover:bg-blue-100">👁️ Voir</button>
                                      <button onClick={(e) => { e.stopPropagation(); validateComponent(order.id, 'site', 'approve'); }} className="px-2 py-1 text-xs bg-green-50 text-green-600 rounded hover:bg-green-100">✅</button>
                                      <button onClick={(e) => { e.stopPropagation(); setRejectModal({ orderId: order.id, component: 'site' }); }} className="px-2 py-1 text-xs bg-red-50 text-red-600 rounded hover:bg-red-100">❌</button>
                                    </div>
                                  )}
                                </div>
                                {info.status === 'rejected' && info.reason && (
                                  <p className="text-xs text-gray-400 italic mt-1.5 ml-7">"{info.reason}"</p>
                                )}
                              </div>
                            );
                          })()}

                          {/* Logo */}
                          {order.logo_url && (() => {
                            const vs = order.validation_status || {};
                            const info = getValidationInfo(vs, 'logo');
                            return (
                              <div className="bg-white rounded-lg p-3">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <span>🎨</span>
                                    <span className="text-sm font-medium">Logo</span>
                                    {info.status === 'approved' && <span className="text-xs text-green-600 font-medium">✅</span>}
                                    {info.status === 'rejected' && <span className="text-xs text-red-600 font-medium">❌ À refaire</span>}
                                  </div>
                                  {info.status === 'pending' && (
                                    <div className="flex gap-1.5">
                                      <button onClick={(e) => { e.stopPropagation(); window.open(resolveLogoUrl(order), '_blank'); }} className="px-2 py-1 text-xs bg-blue-50 text-blue-600 rounded hover:bg-blue-100">👁️ Voir</button>
                                      <button onClick={(e) => { e.stopPropagation(); validateComponent(order.id, 'logo', 'approve'); }} className="px-2 py-1 text-xs bg-green-50 text-green-600 rounded hover:bg-green-100">✅</button>
                                      <button onClick={(e) => { e.stopPropagation(); setRejectModal({ orderId: order.id, component: 'logo' }); }} className="px-2 py-1 text-xs bg-red-50 text-red-600 rounded hover:bg-red-100">❌</button>
                                    </div>
                                  )}
                                </div>
                                {info.status === 'rejected' && info.reason && (
                                  <p className="text-xs text-gray-400 italic mt-1.5 ml-7">"{info.reason}"</p>
                                )}
                              </div>
                            );
                          })()}

                          {/* Google My Business — unified section */}
                          {(addons.includes('gmb_creation') || addons.includes('gmb')) && (() => {
                            const gmbStatus = order.gmb_status || 'pending';
                            const gmbLabels: Record<string, { label: string; color: string; icon: string }> = {
                              'pending': { label: 'Pas de fiche', color: 'text-gray-500', icon: '⏳' },
                              'creating': { label: 'En création', color: 'text-blue-600', icon: '🔄' },
                              'awaiting_verification': { label: 'En attente de vérification', color: 'text-orange-600', icon: '📬' },
                              'verified': { label: 'Vérifiée', color: 'text-green-600', icon: '✅' },
                              'error': { label: 'Erreur', color: 'text-red-600', icon: '❌' },
                            };
                            const statusInfo = gmbLabels[gmbStatus] || gmbLabels['pending'];
                            const missingAddress = !order.location || order.location.trim().length < 5;
                            const missingPhone = !order.phone || order.phone.trim().length < 6;
                            const missingFields: string[] = [];
                            if (missingAddress) missingFields.push('Adresse complète');
                            if (missingPhone) missingFields.push('Numéro de téléphone');
                            if (!order.business_name) missingFields.push('Nom de l\'entreprise');
                            const canCreate = missingFields.length === 0;
                            return (
                              <div className="bg-white rounded-lg p-3">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <span>📍</span>
                                    <span className="text-sm font-medium">Fiche Google My Business</span>
                                    <span className={`text-xs font-medium ${statusInfo.color}`}>
                                      {statusInfo.icon} {statusInfo.label}
                                    </span>
                                  </div>
                                  {(gmbStatus === 'error' || gmbStatus === 'pending') && canCreate && (
                                    <button
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        try {
                                          const record = await pb.collection('jobs').create({
                                            submission_id: order.id,
                                            type: 'addon_gmb_create',
                                            description: 'Relance création fiche Google Business',
                                            status: 'queued',
                                            priority: 1
                                          });
                                          if (record?.id) alert('Job GMB relancé !');
                                        } catch (err: any) {
                                          alert('Erreur: ' + (err.message || err));
                                        }
                                      }}
                                      className="px-2 py-1 text-xs bg-orange-50 text-orange-600 rounded hover:bg-orange-100"
                                    >
                                      🔄 {gmbStatus === 'error' ? 'Relancer' : 'Créer la fiche'}
                                    </button>
                                  )}
                                </div>
                                <div className="mt-2 ml-7 space-y-1">
                                  <p className="text-xs text-gray-500">
                                    <span className="font-medium">Nom :</span> {order.business_name || <span className="text-red-500">Manquant</span>}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    <span className="font-medium">Adresse :</span> {!missingAddress ? (order.location || order.city) : <span className="text-red-500">Manquante</span>}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    <span className="font-medium">Tél :</span> {!missingPhone ? order.phone : <span className="text-red-500">Manquant</span>}
                                  </p>
                                  {missingFields.length > 0 && (
                                    <div className="mt-2 p-2 bg-red-50 border border-red-100 rounded-lg">
                                      <p className="text-xs text-red-600 font-semibold mb-1">⚠️ Infos manquantes pour créer la fiche :</p>
                                      <ul className="text-xs text-red-500 ml-3 space-y-0.5">
                                        {missingFields.map(f => <li key={f}>• {f}</li>)}
                                      </ul>
                                      <button
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          try {
                                            const res = await fetch(`${API_URL}/admin/request-missing-info`, {
                                              method: 'POST',
                                              headers: { 'Content-Type': 'application/json' },
                                              body: JSON.stringify({
                                                submissionId: order.id,
                                                missingFields,
                                                adminKey: ADMIN_KEY
                                              })
                                            });
                                            const data = await res.json();
                                            if (data.ok) {
                                              alert('📧 Email envoyé au client pour demander les infos manquantes');
                                            } else {
                                              alert('Erreur: ' + (data.error || 'Inconnu'));
                                            }
                                          } catch (err: any) {
                                            alert('Erreur: ' + err.message);
                                          }
                                        }}
                                        className="mt-2 px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 font-medium"
                                      >
                                        📧 Demander les infos au client
                                      </button>
                                    </div>
                                  )}
                                  {order.gmb_location_id && (
                                    <p className="text-xs text-gray-400 mt-1">ID: {order.gmb_location_id}</p>
                                  )}
                                  {order.gmb_verified_at && (
                                    <p className="text-xs text-green-500 mt-1">Vérifiée le {new Date(order.gmb_verified_at).toLocaleDateString('fr-FR')}</p>
                                  )}
                                </div>
                              </div>
                            );
                          })()}

                          {/* Pack Visibilité */}
                          {addons.includes('visibility_standard') && (() => {
                            const kw = visibilityKeywords[order.id] || ['', '', ''];
                            const isSaving = keywordSaving[order.id];
                            return (
                              <div className="bg-white rounded-lg p-3">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <span>🚀</span>
                                    <span className="text-sm font-medium">Pack Visibilité</span>
                                    <span className="text-xs text-purple-600 font-medium">19€/mois</span>
                                  </div>
                                </div>
                                <div className="mt-3 ml-7">
                                  <p className="text-xs text-gray-500 font-medium mb-2">Mots-clés à tracker (2-3 max) :</p>
                                  <div className="space-y-1.5">
                                    {[0, 1, 2].map(i => (
                                      <input
                                        key={i}
                                        type="text"
                                        value={kw[i] || ''}
                                        onChange={(e) => {
                                          const updated = [...kw];
                                          while (updated.length < 3) updated.push('');
                                          updated[i] = e.target.value;
                                          setVisibilityKeywords(prev => ({ ...prev, [order.id]: updated }));
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        placeholder={`Mot-clé ${i + 1} (ex: ${['boulangerie lyon', 'coiffeur marseille', 'plombier paris'][i]})`}
                                        className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-200"
                                      />
                                    ))}
                                  </div>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      saveVisibilityKeywords(order.id, visibilityKeywords[order.id] || []);
                                    }}
                                    disabled={isSaving}
                                    className="mt-2 px-3 py-1.5 text-xs bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 font-medium disabled:opacity-50"
                                  >
                                    {isSaving ? '⏳ Sauvegarde...' : '💾 Sauvegarder les mots-clés'}
                                  </button>
                                </div>
                              </div>
                            );
                          })()}

                          {/* Addons dynamiques (excluding those with dedicated sections) */}
                          {addons.filter(a => !['logo', 'domain', 'gmb', 'gmb_creation', 'visibility_standard'].includes(a)).map(addon => {
                            const vs = order.validation_status || {};
                            const info = getValidationInfo(vs, addon);

                            // Blog addon: rich card with article previews
                            if (addon === 'blog') {
                              const articles = blogArticles[order.id] || [];
                              const isLoadingArticles = blogLoading[order.id];
                              return (
                                <div key={addon} className="bg-white rounded-lg p-3">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <span>📝</span>
                                      <span className="text-sm font-medium">Blog</span>
                                      <span className="text-xs text-gray-400">
                                        {isLoadingArticles ? '(chargement...)' : `(${articles.length} article${articles.length > 1 ? 's' : ''})`}
                                      </span>
                                      {info.status === 'approved' && <span className="text-xs text-green-600 font-medium">✅</span>}
                                      {info.status === 'rejected' && <span className="text-xs text-red-600 font-medium">❌ À refaire</span>}
                                    </div>
                                    {info.status === 'pending' && (
                                      <div className="flex gap-1.5">
                                        {order.site_url && (
                                          <button onClick={(e) => { e.stopPropagation(); window.open(`${order.site_url}/actualite`, '_blank'); }} className="px-2 py-1 text-xs bg-blue-50 text-blue-600 rounded hover:bg-blue-100">👁️ Voir blog</button>
                                        )}
                                        <button onClick={(e) => { e.stopPropagation(); validateComponent(order.id, 'blog', 'approve'); }} className="px-2 py-1 text-xs bg-green-50 text-green-600 rounded hover:bg-green-100">✅</button>
                                        <button onClick={(e) => { e.stopPropagation(); setRejectModal({ orderId: order.id, component: 'blog' }); }} className="px-2 py-1 text-xs bg-red-50 text-red-600 rounded hover:bg-red-100">❌</button>
                                      </div>
                                    )}
                                  </div>
                                  {info.status === 'rejected' && info.reason && (
                                    <p className="text-xs text-gray-400 italic mt-1.5 ml-7">"{info.reason}"</p>
                                  )}
                                  {/* Article previews */}
                                  {articles.length > 0 && (
                                    <div className="mt-2 space-y-1.5 ml-7">
                                      {articles.map((article: any) => (
                                        <div key={article.id} className="flex items-start gap-2 p-2 bg-gray-50 rounded-lg">
                                          <div className="flex-1 min-w-0">
                                            <p className="text-xs font-medium text-gray-700 truncate">{article.title}</p>
                                            {article.excerpt && (
                                              <p className="text-xs text-gray-400 truncate mt-0.5">{article.excerpt}</p>
                                            )}
                                          </div>
                                          {order.site_url && article.slug && (
                                            <button
                                              onClick={(e) => { e.stopPropagation(); window.open(`${order.site_url}/actualite/${article.slug}`, '_blank'); }}
                                              className="text-xs text-blue-500 hover:text-blue-700 shrink-0"
                                            >
                                              👁️
                                            </button>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            }

                            // Other addons: generic card
                            return (
                              <div key={addon} className="bg-white rounded-lg p-3">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <span>{addon === 'booking' ? '📅' : addon === 'visibility' ? '📣' : addon === 'form' ? '📋' : addon === 'gmb' || addon === 'gmb_creation' ? '📍' : '📦'}</span>
                                    <span className="text-sm font-medium">{addonLabels[addon] || addon}</span>
                                    {info.status === 'approved' && <span className="text-xs text-green-600 font-medium">✅</span>}
                                    {info.status === 'rejected' && <span className="text-xs text-red-600 font-medium">❌ À refaire</span>}
                                  </div>
                                  {info.status === 'pending' && (
                                    <div className="flex gap-1.5">
                                      <button onClick={(e) => { e.stopPropagation(); validateComponent(order.id, addon, 'approve'); }} className="px-2 py-1 text-xs bg-green-50 text-green-600 rounded hover:bg-green-100">✅</button>
                                      <button onClick={(e) => { e.stopPropagation(); setRejectModal({ orderId: order.id, component: addon }); }} className="px-2 py-1 text-xs bg-red-50 text-red-600 rounded hover:bg-red-100">❌</button>
                                    </div>
                                  )}
                                </div>
                                {info.status === 'rejected' && info.reason && (
                                  <p className="text-xs text-gray-400 italic mt-1.5 ml-7">"{info.reason}"</p>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        {/* Tout valider */}
                        <div className="mt-3 pt-3 border-t border-orange-200 flex justify-end">
                          <button
                            onClick={(e) => { e.stopPropagation(); validateAll(order.id); }}
                            className="px-4 py-2 bg-green-500 text-white rounded-lg text-sm font-semibold hover:bg-green-600 transition-colors"
                          >
                            ✅ Tout valider et mettre en ligne
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Rejection modal */}
      {rejectModal && (
        <RejectModal
          component={rejectModal.component}
          onCancel={() => setRejectModal(null)}
          onConfirm={(reason) => {
            validateComponent(rejectModal.orderId, rejectModal.component, 'reject', reason);
            setRejectModal(null);
          }}
        />
      )}
    </div>
  );
}
